import React from "react";
import { Clock, MapPin, Phone } from "lucide-react";
import { useSection } from "@/context/SiteContext";
import { telHref } from "@/lib/site";

export default function OpeningHours() {
  const hours = useSection("hours");
  const contact = useSection("contact");
  const tel = telHref(contact.phone_tel, contact.phone);

  const rows = [
    { days: hours.weekdays_days || "Måndag – Torsdag", time: hours.weekdays_time || "11:00 – 21:00" },
    { days: hours.friday_days || "Fredag", time: hours.friday_time || "11:00 – 22:00" },
    { days: hours.saturday_days || "Lördag", time: hours.saturday_time || "12:00 – 22:00" },
    { days: hours.sunday_days || "Söndag", time: hours.sunday_time || "12:00 – 21:00" },
  ];

  // Highlight today's row
  const jsDay = new Date().getDay(); // 0=sun ... 6=sat
  const todayIndex = jsDay === 5 ? 1 : jsDay === 6 ? 2 : jsDay === 0 ? 3 : 0;

  return (
    <section id="oppettider" className="py-20 sm:py-24 bg-[var(--bg)]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 sm:mb-14">
          <span className="text-xs uppercase tracking-[0.25em] text-[var(--primary)] font-bold inline-flex items-center gap-2">
            <Clock size={14} /> {hours.eyebrow || "Öppettider"}
          </span>
          <h2 className="font-serif-display text-4xl sm:text-5xl font-black mt-3">
            {hours.title || "När vi är här"}
          </h2>
          {hours.subtitle && (
            <p className="text-[var(--text-muted)] mt-3 max-w-2xl mx-auto">{hours.subtitle}</p>
          )}
        </div>

        <div
          data-testid="opening-hours-card"
          className="bg-white border border-[var(--border)] rounded-3xl overflow-hidden shadow-xl shadow-[var(--primary)]/10 max-w-3xl mx-auto"
        >
          <ul className="divide-y divide-[var(--border)]">
            {rows.map((row, i) => {
              const isToday = i === todayIndex;
              return (
                <li
                  key={row.days}
                  data-testid={`opening-hours-row-${i}`}
                  className={`flex items-center justify-between px-6 sm:px-8 py-5 ${
                    isToday ? "bg-[var(--primary)]/5" : ""
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {isToday && (
                      <span className="rounded-full bg-[var(--primary)] text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">
                        Idag
                      </span>
                    )}
                    <span className={`font-semibold ${isToday ? "text-[var(--primary)]" : ""}`}>
                      {row.days}
                    </span>
                  </div>
                  <span className="font-serif-display text-lg sm:text-xl font-black tabular-nums">
                    {row.time}
                  </span>
                </li>
              );
            })}
          </ul>
          {hours.note && (
            <div className="px-6 sm:px-8 py-4 bg-[var(--surface-alt)] border-t border-[var(--border)] text-sm text-[var(--text-muted)] italic">
              {hours.note}
            </div>
          )}
        </div>

        <div className="mt-8 flex flex-wrap justify-center gap-3 text-sm">
          {contact.address && (
            <span className="inline-flex items-center gap-2 rounded-full bg-white border border-[var(--border)] px-4 py-2 text-[var(--text-muted)]">
              <MapPin size={14} /> {contact.address}
            </span>
          )}
          <a
            href={tel}
            data-testid="hours-call-btn"
            className="inline-flex items-center gap-2 rounded-full bg-[var(--text)] hover:bg-[var(--text)]/85 text-white px-4 py-2 font-semibold"
          >
            <Phone size={14} /> {contact.phone || "0660-511 30"}
          </a>
        </div>
      </div>
    </section>
  );
}
